"use client"

import { observer, use$ } from "@legendapp/state/react"
import { Avatar, Group, Stack, Text } from "@mantine/core"
import { Spotlight } from "@mantine/spotlight"
import classes from "./HeaderSearchSpotlightActions.module.css"
import { useMemo, useTransition } from "react"
import { isValidPhoneNumber, parsePhoneNumberWithError } from "libphonenumber-js"
import { validate as isValidEmail } from "email-validator"
import { showNotification } from "@/lib/notifications"
import { useTranslations } from "next-intl"
import { actionSmartLoadOrCreateContact } from "@/actions/smartLoadOrCreateContact.action"
import { IconPhoneRinging, IconUserPlus, IconMessage } from "@tabler/icons-react"
import { chatStore$ } from "@/lib/ContactsMap"
import type { TContact } from "@/schemas/contacts"
import { selectContact } from "@/components/messenger/contacts-section/helpers"
import { useWebphoneMessage } from "@/hooks/useWebphoneMessage"
import { useChromeExtensionReadinessGate } from "@/components/webphone/useChromeExtensionReadinessGate"
import { syncStore$ } from "@/stores/sync.v2.store"
import { headerSearchSpotlight } from "./HeaderSearchSpotlight"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { useAppStoreContext } from "@/stores/appStore"

interface Props {
  query: string
  isMessengerPage: boolean
}

// Helper to extract just digits and format for calling
function extractPhoneDigits(query: string): string {
  // Remove all non-digit characters except leading +
  const digits = query.replace(/[^\d+]/g, "")
  return digits
}

export const HeaderSearchSpotlightActions = observer(({ query, isMessengerPage }: Props) => {
  const t = useTranslations("noContactsList")
  const [isPending, startTransition] = useTransition()
  // @deprecated The embedded frontend webphone is disabled. Opening it from search is a no-op.
  // const { setWebphoneExpanded } = useAppStoreContext((s) => s)

  // Check if this is a valid phone number (complete)
  const isSearchValidPhoneNumber = useMemo(
    () =>
      isValidPhoneNumber(query ?? "", {
        defaultCountry: "US",
      }),
    [query]
  )

  // For actions, we show them when there are 7+ digits even if not "valid"
  const phoneDigits = useMemo(() => extractPhoneDigits(query), [query])

  const isSearchValidEmail = useMemo(() => isValidEmail(query ?? ""), [query])

  const userID = use$(() => syncStore$.userStatus.id.get())
  const chromeExtensionId = use$(() => syncStore$.cluster.chromeExtensionWebstoreId.get())
  const { ensureReady } = useChromeExtensionReadinessGate()

  const { sendMessage } = useWebphoneMessage({
    onError: () => {
      showNotification({
        type: "error",
        title: t("errors.action_failed.title"),
        message: t("errors.action_failed.message"),
      })
    },
  })

  // Format the phone number for display
  const displayPhoneNumber = useMemo(() => {
    try {
      const phoneNumber = parsePhoneNumberWithError(query, {
        defaultCountry: "US",
      })
      return phoneNumber.formatInternational()
    } catch {
      // If parsing fails, just return the digits
      return phoneDigits
    }
  }, [query, phoneDigits])

  async function handleCall() {
    const isReady = await ensureReady({
      extensionId: chromeExtensionId,
      featureName: "Calling from search",
    })
    if (!isReady) return

    const didSend = await sendMessage({
      from: "ringio-parent-window",
      action: "c2cSmartPlaceCall",
      data: {
        dstNumber: phoneDigits,
        To: phoneDigits,
        srcPersonId: userID,
        origin: "djui",
      },
    })
    if (!didSend) return

    headerSearchSpotlight.close()
  }

  async function handleSendSMS() {
    const isReady = await ensureReady({
      extensionId: chromeExtensionId,
      featureName: "Sending SMS from search",
    })
    if (!isReady) return

    const didSend = await sendMessage({
      from: "ringio-parent-window",
      action: "c2cSmartSendSms",
      data: {
        dstNumber: phoneDigits,
        To: phoneDigits,
        srcPersonId: userID,
        origin: "djui",
      },
    })
    if (!didSend) return

    headerSearchSpotlight.close()
  }

  function handleCreateContact() {
    startTransition(async () => {
      try {
        const contactData = await actionSmartLoadOrCreateContact({
          ...(isSearchValidPhoneNumber
            ? {
                toNumber: query,
              }
            : isSearchValidEmail
              ? {
                  toEmail: query,
                }
              : // If not valid but has digits, still try to create with the digits
                phoneDigits.length >= 7
                ? {
                    toNumber: phoneDigits,
                  }
                : {}),
        })
        chatStore$.searchChats.set([contactData.id.toString()])
        const newContact: TContact = {
          ContactPersonId: contactData.id.toString(),
          ContactFullname: contactData.name,
          AccountId: "",
          ContactEmail: contactData.email,
          ContactCompanyName: contactData.company,
          ContactDNC: false,
          ContactPhoneNumber: "",
          LastTimestamp: 0,
          Occurrences: 0,
          TTL: 0,
          SearchTerm: "",
        }
        chatStore$.contacts[contactData.id.toString()].set(newContact)

        await selectContact({
          contactPersonID: contactData.id.toString(),
          lastInteraction: null,
        })

        // @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns the webphone sidebar.
        // if (!isMessengerPage) {
        //   setWebphoneExpanded(true)
        // }

        headerSearchSpotlight.close()
      } catch {
        showNotification({
          type: "error",
          title: t("mode.search.errors.create_contact.title", { searchTerm: query ?? "" }),
          message: t("mode.search.errors.create_contact.message"),
        })
      }
    })
  }

  return (
    <>
      {/* Call action - shown when we have 7+ digits */}
      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={handleCall}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="blue" classNames={{ placeholder: classes.icon }}>
            <IconPhoneRinging size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              {t("mode.search.buttons.call_contact.title", {
                phone: displayPhoneNumber,
              })}
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      {/* SMS action - shown when we have 7+ digits */}
      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={handleSendSMS}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="green" classNames={{ placeholder: classes.icon }}>
            <IconMessage size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              {t("mode.search.buttons.sms_contact.title", {
                phone: displayPhoneNumber,
              })}
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>

      {/* Create contact action */}
      <Spotlight.Action
        classNames={{
          action: classes.container,
        }}
        onClick={handleCreateContact}
      >
        <Group wrap="nowrap" w="100%">
          <Avatar size="md" color="violet" classNames={{ placeholder: classes.icon }}>
            <IconUserPlus size={16} />
          </Avatar>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={550} className={classes.text}>
              {t("mode.search.buttons.create_contact.title")}
            </Text>
          </Stack>
        </Group>
      </Spotlight.Action>
    </>
  )
})
